import _ from 'lodash';
import { DateTime } from 'luxon';
import { CADENCES, SCHEDULE_EVENT_TYPES } from '../constants/schedule.constants';

export function isRecurringEvent(event) {
  return (
    event?.eventType === SCHEDULE_EVENT_TYPES.EDUCATIONAL && !_.isEmpty(event?.scheduleEventGroupId)
  );
}

// Cadence → CREATE_EDUCATIONAL_EVENT language key labelling it.
export const CADENCE_LABEL_KEYS = {
  [CADENCES.DAILY]: 'REPEAT_DAILY',
  [CADENCES.WEEKLY]: 'REPEAT_WEEKLY',
  [CADENCES.BI_WEEKLY]: 'REPEAT_BI_WEEKLY',
  [CADENCES.MONTHLY]: 'REPEAT_MONTHLY',
  [CADENCES.QUARTERLY]: 'REPEAT_QUARTERLY',
};

// Mirrors the backend's DAY_STEP (schedule.constants.js); monthly cadences step by month instead.
const DAY_STEP = {
  [CADENCES.DAILY]: 1,
  [CADENCES.WEEKLY]: 7,
  [CADENCES.BI_WEEKLY]: 14,
};

const MONTH_STEP = {
  [CADENCES.MONTHLY]: 1,
  [CADENCES.QUARTERLY]: 3,
};

export const RECURRENCE_ORDINAL_LAST = 'LAST';

// Mirrors the backend's `validateRecurrenceWindow`; a longer series is rejected there.
export const MAX_RECURRENCE_YEARS = 2;

/**
 * Where a monthly or quarterly series falls in each month: the start date's weekday and which
 * one of that weekday it is. A fifth weekday reads as the last, as the backend derives it.
 *
 * @param {string} startDate - The series' first date, `YYYY-MM-DD`.
 * @returns {{ weekday: number, ordinal: number | 'LAST' } | null} Luxon weekday (1 = Monday) and
 *   ordinal 1 to 4 or `RECURRENCE_ORDINAL_LAST`; null for an invalid date.
 * @example
 * recurrenceWeekdayParts('2025-09-22'); // { weekday: 1, ordinal: 4 }
 */
export function recurrenceWeekdayParts(startDate) {
  const start = DateTime.fromISO(startDate || '');
  if (!start.isValid) return null;
  const ordinal = Math.ceil(start.day / 7);
  return { weekday: start.weekday, ordinal: ordinal === 5 ? RECURRENCE_ORDINAL_LAST : ordinal };
}

const nthWeekdayOfMonth = (monthStart, weekday, ordinal) => {
  const firstOfMonth = monthStart.startOf('month');
  const first = firstOfMonth.plus({ days: (weekday - firstOfMonth.weekday + 7) % 7 });
  if (ordinal !== RECURRENCE_ORDINAL_LAST) return first.plus({ days: (ordinal - 1) * 7 });
  let candidate = first;
  while (candidate.plus({ days: 7 }).month === candidate.month) {
    candidate = candidate.plus({ days: 7 });
  }
  return candidate;
};

/**
 * The dates a series materializes on, as the backend's `occurrenceDates` generates them: daily,
 * weekly and bi-weekly step by days; monthly and quarterly land on the start date's weekday
 * position in the month ("the fourth Monday").
 *
 * @param {{ cadence: string, startDate: string, endDate: string }} rule - Dates are `YYYY-MM-DD`,
 *   both inclusive.
 * @returns {string[]} The occurrence dates in order; empty for an unknown cadence, a missing or
 *   invalid date, or an end before the start.
 * @example
 * recurrenceOccurrenceDates({ cadence: 'WEEKLY', startDate: '2025-09-22', endDate: '2025-10-06' });
 * // ['2025-09-22', '2025-09-29', '2025-10-06']
 */
export function recurrenceOccurrenceDates({ cadence, startDate, endDate }) {
  if (!startDate || !endDate) return [];
  const start = DateTime.fromISO(startDate);
  const end = DateTime.fromISO(endDate);
  if (!start.isValid || !end.isValid || end < start) return [];

  const dates = [];
  if (DAY_STEP[cadence]) {
    for (let day = start; day <= end; day = day.plus({ days: DAY_STEP[cadence] })) {
      dates.push(day.toISODate());
    }
    return dates;
  }
  if (!MONTH_STEP[cadence]) return [];
  const { weekday, ordinal } = recurrenceWeekdayParts(startDate);
  for (
    let month = start.startOf('month');
    month <= end;
    month = month.plus({ months: MONTH_STEP[cadence] })
  ) {
    const occurrence = nthWeekdayOfMonth(month, weekday, ordinal);
    if (occurrence >= start && occurrence <= end) dates.push(occurrence.toISODate());
  }
  return dates;
}

/**
 * Counts a series' occurrences from startDate through endDate (inclusive), on the dates the
 * backend materializes.
 *
 * @param {{ cadence: string, startDate: string, endDate: string }} rule - Dates are `YYYY-MM-DD`.
 * @returns {number} The count; 0 for an unknown cadence, missing dates, or end before start.
 * @example
 * countRecurrenceOccurrences({ cadence: 'DAILY', startDate: '2025-09-22', endDate: '2025-09-24' }); // 3
 */
export function countRecurrenceOccurrences({ cadence, startDate, endDate }) {
  return recurrenceOccurrenceDates({ cadence, startDate, endDate }).length;
}

/**
 * The end date that makes a series run exactly `count` events: the date of its last occurrence.
 * The inverse of `countRecurrenceOccurrences`, since the backend stores an end date, not a count.
 *
 * @param {{ cadence: string, startDate: string, count: number }} rule - `startDate` is
 *   `YYYY-MM-DD`; `count` is a whole number of at least 1.
 * @returns {string} The end date, `YYYY-MM-DD`; empty for an unknown cadence, an invalid start or
 *   a count below 1.
 * @example
 * recurrenceEndDateForCount({ cadence: 'WEEKLY', startDate: '2025-09-22', count: 14 }); // '2025-12-22'
 */
export function recurrenceEndDateForCount({ cadence, startDate, count }) {
  const start = DateTime.fromISO(startDate || '');
  if (!start.isValid || !Number.isInteger(count) || count < 1) return '';
  if (DAY_STEP[cadence]) return start.plus({ days: DAY_STEP[cadence] * (count - 1) }).toISODate();
  if (!MONTH_STEP[cadence]) return '';
  const { weekday, ordinal } = recurrenceWeekdayParts(startDate);
  const lastMonth = start.startOf('month').plus({ months: MONTH_STEP[cadence] * (count - 1) });
  return nthWeekdayOfMonth(lastMonth, weekday, ordinal).toISODate();
}

/**
 * The latest end date a series starting on `startDate` may have.
 *
 * @param {string} startDate - `YYYY-MM-DD`.
 * @returns {string} `startDate` plus `MAX_RECURRENCE_YEARS`, `YYYY-MM-DD`; empty for an invalid date.
 * @example
 * maxRecurrenceEndDate('2025-09-22'); // '2027-09-22'
 */
export function maxRecurrenceEndDate(startDate) {
  const start = DateTime.fromISO(startDate || '');
  return start.isValid ? start.plus({ years: MAX_RECURRENCE_YEARS }).toISODate() : '';
}
